import { ConnectDragPreview, ConnectDragSource, useDrag } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { DragSourceType, EntityType } from 'const';
import { toggleDragState } from 'containers/App/actions';
import { documentIdsAreBeingUploaded as documentIdsAreBeingUploadedSelector } from 'containers/Project/selectors';
import { GroupLayoutDocumentMap, LayoutDragObject, ReusableLayoutMap, ScreenMap } from 'models';
import { isGroupLayoutDocument } from 'utils/entityType';
import { isGroupReusableLayoutValid, isReusableLayoutValid } from './utils';

type DragSourceHook = {
  connectDragSource: ConnectDragSource;
  connectDragPreview: ConnectDragPreview;
  isDragging: boolean;
};

type Props = {
  document: ReusableLayoutMap | GroupLayoutDocumentMap;
  storyCardId: string;
  screen?: ScreenMap;
  isReadOnly?: boolean;
};

type DragSourceHookProps = Props & { isLayoutBeingUploaded: boolean };

const useReusableLayoutListItemDragSource = (props: DragSourceHookProps): DragSourceHook => {
  const { document, storyCardId, screen, isReadOnly, isLayoutBeingUploaded } = props;
  const dispatch = useDispatch();
  const dragSourceType = DragSourceType.REUSABLE_LAYOUT;
  const componentType = EntityType.LAYOUT;
  const [{ isDragging }, connectDragSource, connectDragPreview] = useDrag<LayoutDragObject, void, { isDragging: boolean }>({
    type: dragSourceType,
    item: () => {
      dispatch(toggleDragState(dragSourceType, componentType));

      return {
        document,
        storyCardId,
        screen,
        isReadOnly,
      };
    },
    end: () => {
      dispatch(toggleDragState());
    },
    canDrag() {
      return !isLayoutBeingUploaded && isReusableLayoutValid(document as ReusableLayoutMap);
    },
    collect(monitor) {
      return {
        isDragging: monitor.isDragging(),
      };
    },
  });

  return { connectDragSource, connectDragPreview, isDragging };

};

const useGroupLayoutListItemDragSource = (props: DragSourceHookProps): DragSourceHook => {
  const { document, storyCardId, screen, isReadOnly, isLayoutBeingUploaded } = props;
  const dispatch = useDispatch();
  const dragSourceType = DragSourceType.GROUP_LAYOUT;
  const componentType = EntityType.LAYOUT;
  const [{ isDragging }, connectDragSource, connectDragPreview] = useDrag<LayoutDragObject, void, { isDragging: boolean }>({
    type: dragSourceType,
    item: () => {
      dispatch(toggleDragState(dragSourceType, componentType));

      return {
        document,
        storyCardId,
        screen,
        isReadOnly,
      };
    },
    end: () => {
      dispatch(toggleDragState());
    },
    canDrag() {
      return !isLayoutBeingUploaded && isGroupReusableLayoutValid(document as GroupLayoutDocumentMap);
    },
    collect(monitor) {
      return {
        isDragging: monitor.isDragging(),
      };
    },
  });

  return { connectDragSource, connectDragPreview, isDragging };
};

type LayoutListItemDragSourceHook = DragSourceHook & { isGroupLayout: boolean; isLayoutBeingUploaded: boolean };

export const useLayoutListItemDragSource = (props: Props): LayoutListItemDragSourceHook => {
  const { document } = props;
  const layoutId = document.get('id');
  const documentIdsAreBeingUploaded = useSelector(documentIdsAreBeingUploadedSelector);
  const isLayoutBeingUploaded = documentIdsAreBeingUploaded.has(layoutId);
  const isGroupLayout = isGroupLayoutDocument(document);
  const useDragSourceHook = isGroupLayout
    ? useGroupLayoutListItemDragSource
    : useReusableLayoutListItemDragSource;

  const { connectDragPreview, connectDragSource, isDragging } = useDragSourceHook({ ...props, isLayoutBeingUploaded });

  return {
    isGroupLayout,
    isLayoutBeingUploaded,
    connectDragPreview,
    connectDragSource,
    isDragging,
  };
};
